import { getCountByStatus as getCountByStatusArtwork } from "@/api/artwork/artwork";
import { getCountByStatus as getCountByStatusAuction } from "@/api/auction/auction";
import { getCountByStatus as getCountByStatusAudiovisual } from "@/api/audiovisual/audiovisual";
import { getCountByStatus as getCountByStatusAuthor } from "@/api/author/author";
import { countByStatus as getCountByStatusBibliography } from "@/api/bibliography/bibliography";
import { getCountByStatus as getCountByStatusCollector } from "@/api/collector/collector";
import { getCountByStatus as getCountByStatusDocument } from "@/api/document/documents";
import { getCountByStatus as getCountByStatusExhibition } from "@/api/exhibition/exhibition";
import { getCountByStatus as getCountByStatusMultipleprint } from "@/api/multipleprint/multipleprint";
import { getCountByStatus as getCountByStatusPhotography } from "@/api/photography/photography";
import { getCountByStatus as getCountByStatusWriting } from "@/api/writing/writing";
import { artworkAdvancedSearchConfig } from "./artwork";
import { audiovisualAdvancedSearchConfig } from "./audiovisual";
import { authorsAdvancedSearchConfig } from "./authors";
import { bibliographyAdvancedSearchConfig } from "./bibliography";
import { collectorsAdvancedSearchConfig } from "./collectors";
import { documentsAdvancedSearchConfig } from "./documents";
import { exhibitionAdvancedSearchConfig } from "./exhibition";
import { multipleprintsAdvancedSearchConfig } from "./multipleprints";
import { photographiesAdvancedSearchConfig } from "./photographies";
import { publicAuctionAdvancedSearchConfig } from "./public-auction";
import { writingsAdvancedSearchConfig } from "./writings";
import { AdvancedSearchConfig } from "../forms/advanced-search/advanced-search-fields-def";

export const getCountByStatusByTable = {
  artwork: getCountByStatusArtwork,
  auction: getCountByStatusAuction,
  audiovisual: getCountByStatusAudiovisual,
  author: getCountByStatusAuthor,
  bibliography: getCountByStatusBibliography,
  collector: getCountByStatusCollector,
  document: getCountByStatusDocument,
  exhibition: getCountByStatusExhibition,
  multipleprint: getCountByStatusMultipleprint,
  photography: getCountByStatusPhotography,
  writing: getCountByStatusWriting,
} as const;

export const artworkExportConfig: AdvancedSearchConfig = [
  ...artworkAdvancedSearchConfig.slice(0, 2),
  {
    type: "group",
    label: "artworksDescriptionDimensions",
    fields: [
      { type: "dimensions", key: "oeuvresDescriptionDimensionsOeuvre" },
      { type: "boolean", key: "oeuvresDescriptionDimensionsOeuvreVerifiees" },
      { type: "boolean", key: "oeuvresDescriptionDimensionsOeuvreInconnues" },
      { type: "boolean", key: "oeuvresDescriptionDimensionsOeuvreDouteuses" },
      {
        type: "boolean",
        key: "oeuvresDescriptionDimensionsHorsMargesVerifiees",
      },
      {
        type: "boolean",
        key: "oeuvresDescriptionDimensionsHorsMargesInconnues",
      },
      {
        type: "boolean",
        key: "oeuvresDescriptionDimensionsHorsMargesDouteuses",
      },
      { type: "dimensions", key: "oeuvresRegieDimensionsCadre" },
      { type: "dimensions", key: "oeuvresRegieDimensionsCaisse" },
    ],
  },
  ...artworkAdvancedSearchConfig.slice(2),
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedAudiovisual" },
      { type: "string", key: "relatedDocument" },
      { type: "string", key: "relatedWriting" },
      { type: "string", key: "relatedPhotography" },
      { type: "string", key: "relatedMultipleprint" },
    ],
  },
];

export const auctionExportConfig: AdvancedSearchConfig = [
  ...publicAuctionAdvancedSearchConfig,
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedAudiovisual" },
      { type: "string", key: "relatedDocument" },
      { type: "string", key: "relatedWriting" },
      { type: "string", key: "relatedPhotography" },
    ],
  },
];

export const audiovisualExportConfig: AdvancedSearchConfig = [
  ...audiovisualAdvancedSearchConfig,
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedDocument" },
      { type: "string", key: "relatedPhotography" },
    ],
  },
];

export const authorExportConfig: AdvancedSearchConfig = [
  ...authorsAdvancedSearchConfig,
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedMultipleprint" },
    ],
  },
];

export const bibliographyExportConfig: AdvancedSearchConfig = [
  ...bibliographyAdvancedSearchConfig.slice(0, 1),
  {
    type: "group",
    label: "bibliographieDescriptionDimensions",
    fields: [{ type: "dimensions", key: "bibliographieDescriptionDimensions" }],
  },
  ...bibliographyAdvancedSearchConfig.slice(1),
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedAuthor" },
      { type: "string", key: "relatedAudiovisual" },
      { type: "string", key: "relatedDocument" },
      { type: "string", key: "relatedPhotography" },
      { type: "string", key: "relatedWriting" },
    ],
  },
];

export const collectorExportConfig: AdvancedSearchConfig = [
  ...collectorsAdvancedSearchConfig,
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedMultipleprint" },
    ],
  },
];

export const documentExportConfig: AdvancedSearchConfig = [
  ...documentsAdvancedSearchConfig.slice(0, 1),
  {
    type: "group",
    label: "documentsDescriptionDimensions",
    fields: [{ type: "dimensions", key: "documentsDescriptionDimensions" }],
  },
  ...documentsAdvancedSearchConfig.slice(1),
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedAudiovisual" },
      { type: "string", key: "relatedWriting" },
    ],
  },
];

export const exhibitionExportConfig: AdvancedSearchConfig = [
  ...exhibitionAdvancedSearchConfig,
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAudiovisual" },
      { type: "string", key: "relatedDocument" },
      { type: "string", key: "relatedWriting" },
      { type: "string", key: "relatedPhotography" },
    ],
  },
];

export const multipleprintExportConfig: AdvancedSearchConfig = [
  ...multipleprintsAdvancedSearchConfig.slice(0, 1),
  {
    type: "group",
    label: "estampesMultiplesDescriptionDimensions",
    fields: [
      {
        type: "dimensions",
        key: "estampesMultiplesDescriptionDimensionsOeuvre",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDimensionsOeuvreVerifiees",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDimensionsOeuvreInconnues",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDimensionsOeuvreDouteuses",
      },
      {
        type: "dimensions",
        key: "estampesMultiplesDescriptionDimensionsImage",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDimensionsImageVerifiees",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDimensionsImageInconnues",
      },
      {
        type: "boolean",
        key: "estampesMultiplesDescriptionDimensionsImageDouteuses",
      },
    ],
  },
  ...multipleprintsAdvancedSearchConfig.slice(1),
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedMultipleprint" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedExhibition" },
    ],
  },
];

export const photographyExportConfig: AdvancedSearchConfig = [
  ...photographiesAdvancedSearchConfig.slice(0, 1),
  {
    type: "group",
    label: "photosDescriptionDimensions",
    fields: [{ type: "dimensions", key: "photosDescriptionDimensions" }],
  },
  ...photographiesAdvancedSearchConfig.slice(1),
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedAudiovisual" },
    ],
  },
];

export const writingExportConfig: AdvancedSearchConfig = [
  ...writingsAdvancedSearchConfig.slice(0, 1),
  {
    type: "group",
    label: "ecritsDescriptionDimensions",
    fields: [{ type: "dimensions", key: "ecritsDescriptionDimensions" }],
  },
  ...writingsAdvancedSearchConfig.slice(1),
  {
    type: "group",
    label: "relatedRecords",
    fields: [
      { type: "string", key: "relatedArtwork" },
      { type: "string", key: "relatedExhibition" },
      { type: "string", key: "relatedBibliography" },
      { type: "string", key: "relatedAuction" },
      { type: "string", key: "relatedAudiovisual" },
    ],
  },
];

export const exportConfigByTable = {
  artwork: artworkExportConfig,
  auction: auctionExportConfig,
  audiovisual: audiovisualExportConfig,
  author: authorExportConfig,
  bibliography: bibliographyExportConfig,
  collector: collectorExportConfig,
  document: documentExportConfig,
  exhibition: exhibitionExportConfig,
  multipleprint: multipleprintExportConfig,
  photography: photographyExportConfig,
  writing: writingExportConfig,
} as const;
